import type { PaypalInfoResponse, VoucherDate, VoucherResponse } from 'src/types/Subscription';
import { getReq, postReq } from '../services/http';
import { getVoucherUrl } from './user_helpers';
import { addDate, dateFormat, fromMillisecondsToDays, timestampToDate } from './date_helper';
import { get } from 'svelte/store';
import { user_data } from 'src/stores/account/user';

export function isPaypalInfo(data: any): data is PaypalInfoResponse {
	return (
		typeof data === 'object' &&
		typeof data.created_time === 'string' &&
		typeof data.free_trial === 'string' &&
		typeof data.last_billing_time === 'string' &&
		typeof data.next_billing_time === 'string' &&
		typeof data.start_time === 'string' &&
		(typeof data.subscription_id === 'string' || typeof data.subscription_id === 'undefined') &&
		typeof data.subscription_status === 'string' &&
		typeof data.subscription_plan_type === 'string'
	);
}

// R.Kovachev
// We have additional fields for later use:
// -nbf (not before) field in the voucher response
// -exp (expiration) field in the voucher response
// -iat (issued at) field in the voucher response
// -aat (activation) field in the voucher response
// -type (premium only for now)

const isValidVoucherInfo = (data: VoucherResponse) => {
	return (
		typeof data === 'object' &&
		typeof data.aat === 'string' &&
		typeof data.duration === 'number' &&
		typeof data.code === 'string' &&
		typeof data.status === 'string'
	);
};

export const hasValidVouchersInfo = (list: VoucherResponse[]) => {
	if (!Array.isArray(list)) {
		return false;
	}

	return list.some(isValidVoucherInfo);
};

export const addDaysToTimestamp = (timestamp, daysToAdd) => {
	const millisecondsPerDay = 24 * 60 * 60 * 1000;
	return timestamp + daysToAdd * millisecondsPerDay;
};

const buildVoucherDates = (voucher: VoucherResponse) => {
	const startDate = timestampToDate(Number(voucher.aat));
	const endDate = addDate(startDate, 'd', voucher.duration, false, false, true);
	const endDateInTimestamp = addDaysToTimestamp(Number(voucher.aat), Number(voucher.duration));

	return {
		start: dateFormat(startDate, 'Y-m-d'),
		end: dateFormat(endDate, 'Y-m-d'),
		daysPassed: fromMillisecondsToDays(dateFormat(endDate, 'Y-m-d'), new Date().toISOString()),
		duration: voucher.duration,
		code: voucher.code || voucher.bulk_code,
		status: endDateInTimestamp < new Date().getTime() ? 'used' : 'active',
		aat: Number(voucher.aat),
		endDateInTimestamp,
	};
};

const normalizeActiveVouchers = (voucherList) => {
	const used = voucherList.filter((v) => v.status === 'used');
	const active = voucherList.filter((v) => v.status === 'active').sort((a, b) => a.aat - b.aat);

	if (active.length <= 1) return voucherList; // nothing to normalize

	// First active voucher stays active
	const normalized = [active[0]];
	let prevEnd = active[0].endDateInTimestamp;

	for (let i = 1; i < active.length; i++) {
		const voucher = active[i];

		const newStart = timestampToDate(addDaysToTimestamp(prevEnd, 1));
		const newEnd = addDate(timestampToDate(prevEnd), 'd', voucher.duration, false, false, true);

		normalized.push({
			...voucher,
			start: dateFormat(newStart, 'Y-m-d'),
			end: dateFormat(newEnd, 'Y-m-d'),
			endDateInTimestamp: addDaysToTimestamp(prevEnd, voucher.duration),
			status: 'on_hold',
			daysPassed: 0,
		});

		prevEnd = addDaysToTimestamp(prevEnd, voucher.duration);
	}

	return [...used, ...normalized].sort((a, b) => a.aat - b.aat);
};

export const buildVouchersDatesList = (vouchers: VoucherResponse | VoucherResponse[]) => {
	const voucherList = Array.isArray(vouchers) ? vouchers : [vouchers];
	const validVouchers = voucherList.filter(isValidVoucherInfo);

	if (validVouchers.length === 0) {
		return [];
	}

	return normalizeActiveVouchers(validVouchers.map(buildVoucherDates));
};

/**
 * Short-lived dedup cache shared by every caller of
 * `getCurrentUnitPremiumInfo` / `getVoucherInfo`. On a single subscriptions
 * page load these helpers are invoked from multiple unrelated sites that
 * cannot easily share a single in-flight promise between them:
 *   1. `http.ts` bootstrap (one-time at app init).
 *   2. `PremiumSettings` (once per mount via `refreshPaypalInfo`).
 *   3. `InvoiceDetails` (independently in its own `onMount`).
 *   4. A second `PremiumSettings` mount caused by `MainLayoutManager`
 *      swapping `<svelte:component>` from `MainLayout` to `MainLayoutNew`
 *      once the `showNewLayout` store hydrates from `false` → `true`
 *      (see `src/stores/move_devices_request.ts` for prior art on this
 *      re-mount).
 * Without dedup we observe up to 4× `get_premium_units` and 2× `all_coupons`
 * per refresh. A small TTL collapses all of these into one request while
 * still letting explicit mutations (cancel subscription, voucher modal
 * close, native focus refresh) bypass it via the `clearXxxCache()`
 * invalidators below.
 */
const DEDUP_TTL_MS = 1500;

let premiumInfoInflight: Promise<any> | null = null;
let premiumInfoCachedAt = 0;

export const clearPremiumInfoCache = (): void => {
	premiumInfoInflight = null;
	premiumInfoCachedAt = 0;
};

/**
 * Рeplacement for {@link getPaypalInfo} based on the new
 * `/user/get_premium_units` endpoint. Returns the PayPal subscription
 * payload for the currently logged-in unit, keeping the same shape as
 * `getPaypalInfo` so call sites don't need any further changes.
 *
 * The new endpoint omits non-premium units and units with no subscription
 * payload, so the current unit may be absent from `data` — in that case we
 * return `{}`, matching the legacy behaviour.
 */
export const getCurrentUnitPremiumInfo = async () => {
	const now = Date.now();
	if (premiumInfoInflight && now - premiumInfoCachedAt < DEDUP_TTL_MS) {
		return premiumInfoInflight;
	}

	premiumInfoCachedAt = now;
	premiumInfoInflight = (async () => {
		try {
			const res = await postReq('/user/get_premium_units', {});
			if (res.isok) {
				const currentUnitId = get(user_data).user_id;
				return res?.data?.[currentUnitId] || {};
			}
		} catch (error) {
			console.error(error);
			premiumInfoInflight = null;
		}
	})();

	return premiumInfoInflight;
};

let voucherInfoInflight: Promise<any> | null = null;
let voucherInfoCachedAt = 0;

export const clearVoucherInfoCache = (): void => {
	voucherInfoInflight = null;
	voucherInfoCachedAt = 0;
};

export const getVoucherInfo = async () => {
	const now = Date.now();
	if (voucherInfoInflight && now - voucherInfoCachedAt < DEDUP_TTL_MS) {
		return voucherInfoInflight;
	}

	voucherInfoCachedAt = now;
	voucherInfoInflight = (async () => {
		try {
			let url = getVoucherUrl();
			const res = await getReq(url + '/coupons/all_coupons', null);
			if (res.isok) {
				return res?.coupons || {};
			}
		} catch (error) {
			console.error(error);
			voucherInfoInflight = null;
		}
		return {};
	})();

	return voucherInfoInflight;
};

export const getTotalVouchersDaysLeft = (vouchersDates: VoucherDate[]) => {
	return vouchersDates.reduce((total, voucher) => {
		if (voucher.status !== 'used') {
			const daysLeft = Math.max(0, voucher.duration - voucher.daysPassed);
			return total + daysLeft;
		} else {
			return total + voucher.duration;
		}
	}, 0);
};

export const addDaysToIsoDate = (isoDate, daysToAdd) => {
	const date = new Date(isoDate);
	date.setUTCDate(date.getUTCDate() + daysToAdd);
	return date.toISOString();
};
